import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, FileText, Globe, Palette, Phone, Users } from 'lucide-react';
import { Banner, Icon, Skeleton } from '../../../ds/components';
import { api, errorMessage } from '../../../lib/api';
import { ChecklistTile, ProgressBanner, type ChecklistTileStatus } from '../ui';
import { organizationKeys, useOrganizationChecklist } from './api';
import { LoadError } from './LoadError';
import type { OrganizationChecklistData } from './types';
import styles from './OrganizationChecklist.module.css';

type Tile = OrganizationChecklistData['tiles'][number];

/** Tile look per server status: telephony is the next action (accent), the payment tile is dashed. */
function statusOf(tile: Tile): ChecklistTileStatus {
  if (tile.status !== 'open') return tile.status;
  if (tile.key === 'telephony') return 'next';
  if (tile.key === 'payment_method') return 'optional';
  return 'open';
}

const openIcons: Record<string, ReactNode> = {
  brand: <Icon icon={Palette} size={16} strokeWidth={1.75} />,
  company_data: <Icon icon={Building2} size={16} strokeWidth={1.75} />,
  legal_texts: <Icon icon={FileText} size={16} strokeWidth={1.75} />,
  domain: <Icon icon={Globe} size={16} strokeWidth={1.75} />,
  team: <Icon icon={Users} size={16} strokeWidth={1.75} />,
  telephony: <Icon icon={Phone} size={16} strokeWidth={1.75} />,
  payment_method: '€',
  first_property: '＋',
};

function iconOf(tile: Tile, status: ChecklistTileStatus): ReactNode {
  if (status === 'done') return '✓';
  if (status === 'blocked') return '!';
  if (status === 'waiting') return '◷';
  return openIcons[tile.key] ?? '＋';
}

/**
 * Onboarding Admin 11 without its page frame (F2 mounts it on `/setup/checklist`): the navy progress banner and the
 * tiles of `GET /setup/checklist/organization`; „Ausblenden" hides the remaining tiles (own).
 */
export function OrganizationChecklist() {
  const queryClient = useQueryClient();
  const checklist = useOrganizationChecklist();
  const [hideError, setHideError] = useState<string | null>(null);

  async function hide() {
    setHideError(null);
    try {
      const next = await api<OrganizationChecklistData>('/setup/checklist/organization/hide', {
        method: 'POST',
        body: {},
      });
      queryClient.setQueryData(organizationKeys.checklist, next);
      await queryClient.invalidateQueries({ queryKey: ['session'] });
    } catch (error) {
      setHideError(errorMessage(error));
    }
  }

  if (checklist.isPending) {
    return (
      <div className={styles.checklist} aria-busy="true" aria-label="Wird geladen">
        <Skeleton variant="block" height={122} />
        <div className={styles.grid}>
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} variant="block" height={170} />
          ))}
        </div>
      </div>
    );
  }
  if (checklist.isError) {
    return (
      <LoadError
        what="Die Checkliste konnte nicht geladen werden"
        error={checklist.error}
        retrying={checklist.isFetching}
        onRetry={() => void checklist.refetch()}
      />
    );
  }
  const data = checklist.data;
  if (!data.visible) return null;
  return (
    <div className={styles.checklist}>
      <ProgressBanner
        tone="navy"
        eyebrow={data.title}
        title={data.subline}
        subline={data.note ?? undefined}
        done={data.progress.done}
        total={data.progress.total}
        onHide={() => void hide()}
      />
      {hideError ? <Banner tone="danger">{hideError}</Banner> : null}
      <div className={styles.grid}>
        {data.tiles.map((tile) => {
          const status = statusOf(tile);
          return (
            <ChecklistTile
              key={tile.key}
              icon={iconOf(tile, status)}
              title={tile.title}
              text={tile.text}
              status={status}
              meta={tile.meta}
              action={tile.action}
            />
          );
        })}
      </div>
    </div>
  );
}
